import React from "react"

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@coval/ui"
import { X } from "lucide-react"

import { ChangeTypeBadge, type VersionChangeType } from "./ChangeTypeBadge"

/**
 * One row in the version-history panel. `changeType` renders a Save/Revert
 * badge when present (omit it for the live/current row, which has no snapshot
 * change type).
 */
export interface VersionListItem {
  key: string
  title: string
  subtitle: string
  changeType?: VersionChangeType
}

const VersionListRow: React.FC<{
  item: VersionListItem
  isSelected: boolean
  onSelect: () => void
}> = ({ item, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={isSelected}
    className={`flex flex-col gap-0.5 rounded-md px-3 py-2.5 text-left transition-colors ${
      isSelected ? "bg-surface-1" : "hover:bg-surface-1"
    }`}
  >
    <span className="flex items-center justify-between gap-2">
      <span className="truncate text-sm font-medium text-coval-text">
        {item.title}
      </span>
      {item.changeType ? (
        <ChangeTypeBadge changeType={item.changeType} />
      ) : null}
    </span>
    <span className="truncate text-xs text-coval-text-muted">
      {item.subtitle}
    </span>
  </button>
)

interface VersionHistoryPanelProps {
  /** The live/current version row, shown at the top. Null while it loads. */
  currentItem: VersionListItem | null
  /** Prior versions, newest-first (already ordered by the caller). */
  versions: VersionListItem[]
  selectedKey: string
  onSelect: (key: string) => void
  onClose: () => void
  isLoading?: boolean
  isError?: boolean
  /** Resource-specific copy shown when there are no prior versions. */
  emptyMessage: string
  title?: string
}

/**
 * Shared version-history side panel, carried by the docked `Drawer`: a
 * "Version History" header with a close button, the current version, and the
 * newest-first list of prior versions (each with an optional Save/Revert
 * badge), plus loading/error/empty states. Docking through the primitive means
 * the panel reserves its width from the app shell rather than sitting in the
 * page's own flex row, and inherits the shared dock chrome — resizable width,
 * remembered per size, and Escape to leave version history.
 *
 * Mounting the panel opens it; callers render it only while version history is
 * on, and `onClose` fires when the drawer asks to close.
 *
 * Presentational only — resource modules map their versions to
 * `VersionListItem`s and own the main read-only content beside the panel.
 */
export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  currentItem,
  versions,
  selectedKey,
  onSelect,
  onClose,
  isLoading = false,
  isError = false,
  emptyMessage,
  title = "Version History",
}) => (
  <Drawer
    open
    onOpenChange={(next) => {
      if (!next) {
        onClose()
      }
    }}
  >
    <DrawerContent size="sm">
      <DrawerHeader
        hideClose
        actions={
          // The drawer's own close button reads only "Close"; version history
          // is a mode the panel exits rather than a sheet it dismisses, so the
          // control keeps its own name.
          <DrawerClose asChild>
            <Button variant="secondary" icon tooltip="Exit version history">
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
        }
      >
        <DrawerTitle>{title}</DrawerTitle>
      </DrawerHeader>
      <DrawerBody className="flex flex-col gap-1 px-2 py-2">
        {currentItem ? (
          <VersionListRow
            item={currentItem}
            isSelected={selectedKey === currentItem.key}
            onSelect={() => onSelect(currentItem.key)}
          />
        ) : null}
        {isLoading ? (
          <p className="px-3 py-2.5 text-sm text-coval-text-muted">
            Loading version history…
          </p>
        ) : isError ? (
          <p className="px-3 py-2.5 text-sm text-coval-danger-text">
            Couldn’t load version history. Please try again.
          </p>
        ) : versions.length === 0 ? (
          <p className="px-3 py-2.5 text-sm text-coval-text-muted">
            {emptyMessage}
          </p>
        ) : (
          versions.map((item) => (
            <VersionListRow
              key={item.key}
              item={item}
              isSelected={selectedKey === item.key}
              onSelect={() => onSelect(item.key)}
            />
          ))
        )}
      </DrawerBody>
    </DrawerContent>
  </Drawer>
)
